import React, { useEffect, useState } from 'react';
import {
  Box,
  Tooltip,
  Button,
  Typography,
  IconButton,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import IntegrationPanelPill from './IntegretionPanelPill';
import { setBlinkByWorkflow } from '../state/slices/mappingSlice';
import Schedule from '../workflows/images/Schedule.png';
import Execute from '../workflows/images/RunFlow.png';
import EditIcon from '../workflows/images/EditIcon.png';
import { updateWorkflowName } from '../apis/Workflows';
import { renameWorkflow } from '../state/slices/workflowSlice';
import { useThemeContext } from '../Context/ThemeContext';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

const MotionBox = motion(Box);

const WorkflowActionBar = ({
  workflowId,
  onSave,
  onRunNow,
  onScheduleOpen,
  enableBacktrack,
  setEnableBacktrack,
}) => {
  const { darkMode } = useThemeContext();
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [nameVal, setNameVal] = useState('');

  const theme = useTheme();
  // const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const allWorkflows = useSelector((s) => s.workflows?.list || []);
  const wf = useSelector((s) =>
    s.workflows?.list?.find((w) => w.workflow_id === workflowId),
  );

  const workflowStoreEntry = useSelector(
    (state) => state.workflows?.workflows?.[workflowId],
  );
  const hasUnsavedChanges = useSelector(
    (state) => state.workflows?.unsavedChangesByWorkflow?.[workflowId],
  );
  const workflowConfigurationId =
    workflowStoreEntry?.workflowConfigurationId ?? null;
  const disableActions = Boolean(hasUnsavedChanges) || !workflowConfigurationId;
  const scheduleButtonTooltip = disableActions
    ? workflowConfigurationId
      ? 'Save your latest changes before scheduling.'
      : 'Save the workflow before scheduling.'
    : undefined;
  const executeButtonTooltip = disableActions
    ? workflowConfigurationId
      ? 'Save your latest changes before executing.'
      : 'Save the workflow before executing.'
    : undefined;

  useEffect(() => {
    setNameVal(wf?.name || '');
  }, [wf?.name]);

  const saveBlink = useSelector(
    (state) => state.mapping.blinkByWorkflow?.[workflowId]?.save,
  );

  const IconCircleButton = ({
    title,
    imgSrc,
    onClick,
    size = 40,
    imgSize = 26,
    disabled = false,
    tooltip,
  }) => {
    const tooltipTitle = tooltip ?? title;
    return (
      <Tooltip title={tooltipTitle} arrow>
        <MotionBox
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={title}
          aria-disabled={disabled}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            onClick?.(e);
          }}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick?.(e);
            }
          }}
          whileHover={disabled ? undefined : { scale: 1.06 }}
          whileTap={disabled ? undefined : { scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 380, damping: 22 }}
          sx={{
            backgroundColor: '#EEF2FF',
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            userSelect: 'none',
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            border: '1px solid rgba(79,172,254,0.2)',
            opacity: disabled ? 0.5 : 1,
            '&:hover': {
              backgroundColor: '#E5E9FF',
              boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
            },
            '&:active': {
              boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
            },
            '&:focus-visible': {
              outline: 'none',
              boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
            },
          }}
        >
          <Box
            component="img"
            src={imgSrc}
            alt={title}
            sx={{
              width: `${imgSize}px`,
              height: `${imgSize}px`,
              pointerEvents: 'none',
            }}
          />
        </MotionBox>
      </Tooltip>
    );
  };

  IconCircleButton.propTypes = {
    title: PropTypes.string.isRequired,
    imgSrc: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    size: PropTypes.number,
    imgSize: PropTypes.number,
    disabled: PropTypes.bool,
    tooltip: PropTypes.string,
  };

  const handleClick = () => {
    dispatch(setBlinkByWorkflow({ workflowId, button: 'save', value: false }));
    dispatch(
      setBlinkByWorkflow({ workflowId, button: 'execute', value: true }),
    );
    onSave();
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleSaveName = async () => {
    const newName = nameVal.trim();
    if (!newName || !workflowId) return;

    const isDuplicate = allWorkflows.some(
      (w) => w.workflow_id !== workflowId && w.name === newName,
    );
    if (isDuplicate) {
      toast.error('Workflow with this name already exists.', {
        autoClose: 3000,
      });
      return;
    }

    if (wf?.name === newName) {
      setEditing(false);
      return;
    }

    try {
      await updateWorkflowName(workflowId, newName);
      dispatch(renameWorkflow({ workflowId, name: newName }));
      toast.success('Workflow renamed successfully', { autoClose: 2000 });
      setEditing(false);
    } catch (e) {
      console.error('Rename failed', e);
    }
  };

  // Responsive icon/button sizes
  const circleSize = isMdDown ? 34 : 40;
  const scheduleImg = isMdDown ? 22 : 25;
  const executeImg = isMdDown ? 26 : 30;
  const editImg = isMdDown ? 22 : 25;

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: { xs: 56, sm: 60 },
          bgcolor: '#f8f6ffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'fixed',
          top: { xs: 40, sm: 40 },
          zIndex: 1202,
          borderBottom: '1px solid rgba(0,0,0,0.04)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'transparent',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            width: '100%',
            mx: { xs: 1.5, sm: 2, md: 4 },
            gap: { xs: 1, sm: 1.5, md: 2 },
            rowGap: { xs: 1, sm: 1 },
          }}
        >
          {/* Left: Back + Name/Rename */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              flex: '1 1 220px', // lets it shrink/grow and wrap
              gap: { xs: 0.25, sm: 0.5 },
              order: 1,
            }}
          >
            <Tooltip title="Go back">
              <IconButton
                onClick={handleBack}
                sx={{
                  color: darkMode ? '#fff' : '#000',
                  p: { xs: '2px', sm: '4px' },
                  '&:hover': {
                    backgroundColor: darkMode ? '#404040' : '#f5f5f5',
                  },
                }}
              >
                <ArrowBackIosIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
              </IconButton>
            </Tooltip>

            {editing ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.4,
                  minWidth: 0,
                }}
              >
                <TextField
                  value={nameVal}
                  onChange={(e) => setNameVal(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') {
                      setNameVal(wf?.name || '');
                      setEditing(false);
                    }
                  }}
                  variant="standard"
                  size="small"
                  autoFocus
                  inputProps={{ maxLength: 80 }}
                  sx={{
                    minWidth: { xs: 120, sm: 140 },
                    maxWidth: { xs: 160, sm: 220, md: 280 },
                    color: darkMode ? '#fff' : 'inherit',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleSaveName}
                  disabled={!nameVal.trim() || nameVal.trim() === wf?.name}
                >
                  <Check fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    setNameVal(wf?.name || '');
                    setEditing(false);
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.5, sm: 1 },
                  minWidth: 0,
                }}
              >
                <Tooltip title={workflowId}>
                  <motion.div
                    initial={{ y: -6, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.28, ease: 'easeOut' }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        fontSize: { xs: 16, sm: 18 },
                        color: darkMode ? '#fff' : 'inherit',
                        maxWidth: { xs: 110, sm: 160, md: 220 },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        px: 1.2,
                      }}
                    >
                      {wf?.name ?? ''}
                    </Typography>
                  </motion.div>
                </Tooltip>
                <IconCircleButton
                  title="Rename"
                  imgSrc={EditIcon}
                  onClick={() => setEditing(true)}
                  size={circleSize}
                  imgSize={editImg}
                />
              </Box>
            )}
          </Box>

          {/* Center: Integration Panel (kept visible; it will wrap under on small screens) */}
          <Box
            sx={{
              flex: '1 1 260px',
              display: 'flex',
              justifyContent: 'center',
              order: { xs: 3, sm: 2 }, // moves to next line on phones, centered
              minWidth: 0,
            }}
          >
            <IntegrationPanelPill />
          </Box>

          {/* Right: Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: { xs: 1.25, sm: 2.5, md: 4 },
              flex: '1 1 260px',
              width: '100%',
              order: { xs: 2, sm: 3 },
              minWidth: 0,
            }}
          >
            {setEnableBacktrack && enableBacktrack && (
              <Tooltip title="Bidirectional is enabled (automatically enabled when Bidirectional node is added)">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mr: { xs: 0.5, sm: 1 },
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: darkMode
                      ? 'rgba(156, 39, 176, 0.2)'
                      : 'rgba(156, 39, 176, 0.1)',
                    border: '1px solid',
                    borderColor: darkMode
                      ? 'rgba(156, 39, 176, 0.3)'
                      : 'rgba(156, 39, 176, 0.2)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      color: darkMode ? '#ce93d8' : '#9c27b6',
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                    }}
                  >
                    ✓ Bidirectional
                  </Typography>
                </Box>
              </Tooltip>
            )}
            <IconCircleButton
              title="Schedule"
              imgSrc={Schedule}
              onClick={onScheduleOpen}
              size={circleSize}
              imgSize={scheduleImg}
              disabled={disableActions}
              tooltip={scheduleButtonTooltip}
            />
            <IconCircleButton
              title="Execute"
              imgSrc={Execute}
              onClick={onRunNow}
              size={circleSize}
              imgSize={executeImg}
              disabled={disableActions}
              tooltip={executeButtonTooltip}
            />
            <Box sx={{ minWidth: 0 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.28, ease: 'easeOut', delay: 0.05 }}
              >
                <Tooltip
                  title="Click to save your changes!"
                  open={saveBlink}
                  placement="bottom"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        mt: '-15px',
                        bgcolor: '#525252',
                        color: '#fff',
                        fontWeight: '500',
                        fontSize: '12px',
                        animation: saveBlink
                          ? 'tooltipPulse 1.5s ease-in-out infinite'
                          : 'none',
                        '@keyframes tooltipPulse': {
                          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                          '50%': { opacity: 0.7, transform: 'scale(1.05)' },
                        },
                      },
                    },
                    arrow: { sx: { color: '#525252' } },
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleClick}
                    sx={{
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      backgroundColor: '#5965A6',
                      px: { xs: 1.25, sm: 1.75 },
                      py: { xs: 0.5, sm: 0.75 },
                      color: 'white',
                      textTransform: 'none',
                      borderRadius: '6px',
                      fontWeight: 500,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      transition:
                        'transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 200ms ease',
                      willChange: 'transform, box-shadow',
                      '&:hover': {
                        backgroundColor: '#4C5B9A',
                        transform: 'translateY(-1px) scale(1.02)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                      },
                      '&:active': {
                        transform: 'translateY(0) scale(0.99)',
                        boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                      },
                      '&:focus-visible': {
                        outline: 'none',
                        boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
                      },
                      // Prevent layout jump if it wraps
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Save
                  </Button>
                </Tooltip>
              </motion.div>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

WorkflowActionBar.propTypes = {
  workflowId: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onRunNow: PropTypes.func.isRequired,
  onScheduleOpen: PropTypes.func.isRequired,
  enableBacktrack: PropTypes.bool,
  setEnableBacktrack: PropTypes.func,
};

export default WorkflowActionBar;
